// src/pages/Roles.js
//
// Perfiles de permisos por empresa (Superadmin, Admin, Cajero, Vendedor,
// Domiciliario POS). Los 5 defaults se siembran al crear la empresa
// (migración 043) y son editables por el admin (menos código y nombre).
//
// Los perfiles se aplican realmente en C10 (empleados de tienda con
// permisos limitados). Por ahora esta pantalla permite definirlos y
// guardarlos; hasPermission() del frontend sigue usando el rol Identity.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, Button, IconButton,
  Grid, Alert, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Divider, Checkbox, FormControlLabel, Tooltip, Avatar,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AddIcon      from '@mui/icons-material/Add';
import EditIcon     from '@mui/icons-material/Edit';
import DeleteIcon   from '@mui/icons-material/Delete';
import RefreshIcon  from '@mui/icons-material/Refresh';
import LockIcon     from '@mui/icons-material/Lock';
import { useApp }  from '../context/AppContext';
import { perfilesPermisos as apiPerf } from '../api/posApi';

const Roles = () => {
  const { empresa, notify } = useApp();
  const empresaId = empresa?.empresaId || empresa?.EmpresaId;

  const [perfiles, setPerfiles] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [tick, setTick] = useState(0);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [p, c] = await Promise.all([apiPerf.listar(empresaId), apiPerf.catalogo()]);
      setPerfiles(Array.isArray(p) ? p : []);
      setCatalogo(Array.isArray(c) ? c : []);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudieron cargar los perfiles.', 'error');
    } finally { setCargando(false); }
  }, [empresaId, notify]);

  useEffect(() => { cargar(); }, [cargar, tick]);

  const eliminar = async (p) => {
    if (!window.confirm(`¿Eliminar el perfil "${p.nombre}"?`)) return;
    try {
      await apiPerf.eliminar(p.perfilId);
      notify('Perfil eliminado.', 'success');
      setTick(t => t + 1);
    } catch (e) { notify(e.mensajeUsuario || 'No se pudo eliminar.', 'error'); }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <AdminPanelSettingsIcon color="primary" fontSize="large" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={700}>Roles y permisos</Typography>
          <Typography variant="body2" color="text.secondary">
            Perfiles configurables por empresa
          </Typography>
        </Box>
        <IconButton onClick={() => setTick(t => t + 1)}><RefreshIcon /></IconButton>
        <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => setDialog({ modo: 'nuevo' })}>
          Nuevo perfil
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Los perfiles del <b>sistema</b> vienen precargados y se pueden editar
        (permisos, color, descripción) pero no eliminar. Los perfiles
        personalizados los puedes eliminar cuando quieras.
      </Alert>

      {cargando && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2}>
        {perfiles.map(p => (
          <Grid item xs={12} sm={6} md={4} key={p.perfilId}>
            <Card sx={{ height: '100%', borderTop: 4, borderColor: p.color || 'primary.main' }}>
              <CardContent>
                <Stack direction="row" alignItems="flex-start" spacing={1.5} mb={1.5}>
                  <Avatar sx={{ bgcolor: p.color || 'primary.main', width: 40, height: 40 }}>
                    <AdminPanelSettingsIcon fontSize="small" />
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h6" fontWeight={600}>{p.nombre}</Typography>
                      {p.esSistema && (
                        <Tooltip title="Perfil del sistema">
                          <LockIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                      {p.codigo}
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 40 }}>
                  {p.descripcion || '(Sin descripción)'}
                </Typography>
                <Chip size="small" label={`${p.permisos?.length || 0} permisos`} />
                {p.permisos?.includes('*') && (
                  <Chip size="small" label="Acceso total" color="error" sx={{ ml: 1 }} />
                )}
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Tooltip title="Editar">
                    <IconButton size="small"
                                onClick={() => setDialog({ modo: 'editar', perfil: p })}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {!p.esSistema && (
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => eliminar(p)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {!cargando && perfiles.length === 0 && (
        <Alert severity="warning">
          No hay perfiles en esta empresa. Corre la migración 043 para sembrar
          los 5 perfiles por defecto, o crea el primero manualmente.
        </Alert>
      )}

      {dialog && (
        <DialogPerfil catalogo={catalogo} perfil={dialog.perfil} modo={dialog.modo}
                      onClose={() => setDialog(null)}
                      onGuardado={() => { setDialog(null); setTick(t => t + 1);
                                           notify(dialog.modo === 'nuevo' ? 'Perfil creado.' : 'Perfil actualizado.', 'success'); }}
                      onError={(m) => notify(m, 'error')} />
      )}
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Dialog editar/crear perfil
// ══════════════════════════════════════════════════════════════════════

function DialogPerfil({ catalogo, perfil, modo, onClose, onGuardado, onError }) {
  const [f, setF] = useState({
    codigo:      perfil?.codigo || '',
    nombre:      perfil?.nombre || '',
    descripcion: perfil?.descripcion || '',
    color:       perfil?.color || '#1976D2',
    permisos:    new Set(perfil?.permisos || []),
    activo:      perfil?.activo ?? true,
  });
  const [g, setG] = useState(false);
  const esSistema = perfil?.esSistema;
  const tieneWildcard = f.permisos.has('*');

  const togglePermiso = (codigo) => {
    setF(s => {
      const nuevo = new Set(s.permisos);
      if (nuevo.has(codigo)) nuevo.delete(codigo);
      else nuevo.add(codigo);
      return { ...s, permisos: nuevo };
    });
  };

  const toggleModulo = (mod, marcar) => {
    setF(s => {
      const nuevo = new Set(s.permisos);
      mod.permisos.forEach(p => marcar ? nuevo.add(p.codigo) : nuevo.delete(p.codigo));
      return { ...s, permisos: nuevo };
    });
  };

  const submit = async () => {
    if (!f.nombre.trim()) { onError('Nombre requerido.'); return; }
    if (modo === 'nuevo' && !f.codigo.trim()) { onError('Código requerido.'); return; }

    setG(true);
    try {
      const dto = {
        codigo: f.codigo.trim().toUpperCase(),
        nombre: f.nombre.trim(),
        descripcion: f.descripcion,
        color: f.color,
        permisos: Array.from(f.permisos),
        activo: f.activo,
      };
      if (modo === 'nuevo') await apiPerf.crear(dto);
      else await apiPerf.actualizar(perfil.perfilId, dto);
      onGuardado();
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo guardar.'); }
    finally { setG(false); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {modo === 'nuevo' ? 'Nuevo perfil' : `Editar perfil: ${perfil.nombre}`}
      </DialogTitle>
      <DialogContent dividers>
        {esSistema && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Este es un perfil del sistema. Puedes editar sus permisos, descripción
            y color, pero no su código ni nombre.
          </Alert>
        )}
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2}>
            <TextField required label="Código" sx={{ width: 200 }}
                       value={f.codigo} disabled={esSistema || modo === 'editar'}
                       onChange={(e) => setF(s => ({ ...s, codigo: e.target.value.toUpperCase() }))} />
            <TextField required fullWidth label="Nombre visible"
                       value={f.nombre} disabled={esSistema}
                       onChange={(e) => setF(s => ({ ...s, nombre: e.target.value }))} />
            <TextField label="Color" type="color" sx={{ width: 90 }}
                       value={f.color}
                       onChange={(e) => setF(s => ({ ...s, color: e.target.value }))} />
          </Stack>
          <TextField label="Descripción" multiline rows={2}
                     value={f.descripcion}
                     onChange={(e) => setF(s => ({ ...s, descripcion: e.target.value }))} />
        </Stack>

        <Divider sx={{ mb: 2 }} />
        <Stack direction="row" alignItems="center" spacing={2} mb={1.5}>
          <Typography variant="subtitle1" fontWeight={600}>Permisos</Typography>
          <FormControlLabel control={
            <Checkbox checked={tieneWildcard}
                      onChange={(e) => setF(s => {
                        const n = new Set(s.permisos);
                        if (e.target.checked) n.add('*'); else n.delete('*');
                        return { ...s, permisos: n };
                      })} />
          } label="Acceso total (*)" />
        </Stack>

        {tieneWildcard ? (
          <Alert severity="warning">
            Con acceso total, este perfil puede realizar cualquier acción.
            Los permisos individuales de abajo se ignoran.
          </Alert>
        ) : (
          <Stack spacing={2}>
            {catalogo.map(mod => {
              const codigos = mod.permisos.map(p => p.codigo);
              const marcados = codigos.filter(c => f.permisos.has(c)).length;
              const todos = marcados === codigos.length;
              const algunos = marcados > 0 && !todos;
              return (
                <Card variant="outlined" key={mod.modulo}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack direction="row" alignItems="center" mb={1}>
                      <FormControlLabel control={
                        <Checkbox
                          checked={todos} indeterminate={algunos}
                          onChange={(e) => toggleModulo(mod, e.target.checked)} />
                      } label={<Typography fontWeight={600}>{mod.modulo}</Typography>} />
                      <Chip size="small" label={`${marcados}/${codigos.length}`} sx={{ ml: 'auto' }} />
                    </Stack>
                    <Grid container spacing={0}>
                      {mod.permisos.map(p => (
                        <Grid item xs={12} sm={6} key={p.codigo}>
                          <FormControlLabel control={
                            <Checkbox size="small" checked={f.permisos.has(p.codigo)}
                                      onChange={() => togglePermiso(p.codigo)} />
                          } label={
                            <Box>
                              <Typography variant="body2">{p.nombre}</Typography>
                              <Typography variant="caption" color="text.disabled" fontFamily="monospace">
                                {p.codigo}
                              </Typography>
                            </Box>
                          } />
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={g}>
          {g ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Roles;
